import { ReactNode, forwardRef } from 'react';
import FormField, { FormFieldProps } from '@adsk/alloy-react-form-field';
import styled from 'styled-components';
import { InfoPopoverProps } from '@adsk/alloy-react-tooltip';
import { InlineLoader, LoaderStyle } from '../InlineLoader';

export type CommonFormFieldProps = Omit<FormFieldProps, 'renderInfoPopover'> & {
  loading?: boolean;
  loaderStyle?: LoaderStyle;
  useMarginBottom?: boolean;
  // Due to a change of offset prop structure in alloy-react-tooltip v.4.0.0, it should be removed from the props
  // When we upgrade to v4+ we should remove this exclusion
  // `offset is not in use in FormField so this removal doesn't affect the functionality
  renderInfoPopover?: (props: Omit<InfoPopoverProps, 'message' | 'offset'>) => ReactNode;
};

const MARGIN_BOTTOM_STYLE = { marginBottom: 16 };

const SpinnerLoadingWrapper = styled.div({
  paddingLeft: '12px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
});

const ContentLoadingWrapper = styled.div({
  height: '36px',
  display: 'flex',
  alignItems: 'center',
});

const renderLoader = (loaderStyle: LoaderStyle) =>
  loaderStyle === LoaderStyle.Spinny ? (
    <SpinnerLoadingWrapper>
      <InlineLoader loaderStyle={loaderStyle} />
    </SpinnerLoadingWrapper>
  ) : (
    <ContentLoadingWrapper>
      <InlineLoader loaderStyle={loaderStyle} />
    </ContentLoadingWrapper>
  );

export const CommonFormField = forwardRef<HTMLDivElement, CommonFormFieldProps>(
  (
    {
      children,
      loading = false,
      loaderStyle = LoaderStyle.Text,
      useMarginBottom = true,
      style,
      ...formFieldProps
    },
    ref,
  ) => (
    <FormField
      {...(useMarginBottom ? { style: { ...MARGIN_BOTTOM_STYLE, ...style } } : { style })}
      labelVariant={FormField.LABEL_VARIANTS.TOP}
      ref={ref}
      {...formFieldProps}
    >
      {loading ? renderLoader(loaderStyle) : children}
    </FormField>
  ),
);
